import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { TextField } from "@mui/material"
import { Loader } from "@components/Loader/loader"
import { login } from "@services/auth/login"
import { notify } from "@utils/notify"
import useAuth from "@hooks/useAuth"
import useUser from "@hooks/useUser"
import { useDispatch } from "react-redux"
import { getTokenLoading, getTokenSuccess } from "@redux/slices/authSlice"
import { InititateFacebookAuth } from "@services/initiateFacebookLogin"
import { magic } from "@utils/Magic"
import verifyMagicOtp from "@services/auth/verifyMagicOtp"
import LoginBg from "@assets/loginBg.svg"
import facebook from "@assets/facebook.svg"

const LoginPage = () => {
  const dispatch = useDispatch()
  const [username, setUsername] = useState("")
  const [password, setPassword] = useState("")
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState({
    username: "",
    password: ""
  })
  const navigate = useNavigate()
  const { handleGetToken } = useAuth()
  const { handleGetUserById } = useUser()

  // Validate email format
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  // Validate input fields
  const validateInputs = () => {
    let isValid = true
    const newErrors = {
      username: "",
      password: ""
    }

    // Validate email
    if (!username) {
      newErrors.username = "Email is required"
      isValid = false
    } else if (!validateEmail(username)) {
      newErrors.username = "Please enter a valid email address"
      isValid = false
    }

    // Validate password
    if (!password) {
      newErrors.password = "Password is required"
      isValid = false
    }

    setErrors(newErrors)
    return isValid
  }

  const getToken = async () => {
    dispatch(getTokenLoading())
    const tokenFromLocalStorage = localStorage.getItem("token")
    if (tokenFromLocalStorage) {
      dispatch(getTokenSuccess(tokenFromLocalStorage))
    } else {
      await handleGetToken()
    }
  }

  useEffect(() => {
    async function checkUserLoggedIn() {
      const storedUserId = localStorage.getItem("userId")
      if (storedUserId) {
        await getToken()
        await handleGetUserById()
        window.dispatchEvent(new Event('userStateChange'));
        // navigate("/launch-new-ad")
        navigate("/quick-launch-ads")
      }
    }
    checkUserLoggedIn()
  }, [navigate, handleGetUserById])

  const loginWithOtp = async () => {
    // Validate email for OTP login
    if (!username) {
      setErrors({...errors, username: "Email is required"});
      notify("Email is required", "error");
      return;
    } else if (!validateEmail(username)) {
      setErrors({...errors, username: "Please enter a valid email address"});
      notify("Please enter a valid email address", "error");
      return;
    }

    try {
      setLoading(true);
      // Get DID token from Magic
      const didToken = await magic.auth.loginWithEmailOTP({ email: username, showUI: true });
      console.log("Magic login successful, DID token:", didToken);
      // Verify the token with our backend
      const response = await verifyMagicOtp(didToken);
      console.log("OTP verification response:", response);

      // Extract userId from response
      const userId = response.userId;

      // Get user info for additional details if needed
      const userInfo = await magic.user.getInfo();
      console.log("User info:", userInfo);

      // Store user data
      localStorage.setItem("actualUsername", username);

      if (userId) {
        localStorage.setItem("userId", userId);
        notify("Login successful", "success");
        await getToken();
        await handleGetUserById();
        window.dispatchEvent(new Event('userStateChange'));
        // navigate("/launch-new-ad");
        navigate("/quick-launch-ads")
      } else {
        notify("Login verification failed", "error");
      }
    } catch (error) {
      console.error("Magic login error:", error);
      notify("Login failed. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateInputs()) {
      return;
    }

    setLoading(true);

    try {
      // 👇 Normal password login
      const response = await login({ username, password });
      const uid = response.data.data.userId;
      localStorage.setItem("actualUsername", username);
      if (uid) {
        // check if 2FA is enabled
        // const is2FAEnabled = response.data.data["2FAEnabled"];
        // if (is2FAEnabled) {
        //   loginWithOtp();
        // } else {
        localStorage.setItem("userId", uid);
        notify("Login successful", "success");
        await getToken();
        await handleGetUserById();
        window.dispatchEvent(new Event('userStateChange'));
        // navigate("/launch-new-ad");
        navigate("/quick-launch-ads")
        // }
      } else {
        notify("Login failed", "error");
      }
    } catch (error) {
      console.error("Login error:", error);
      notify("Login failed", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleFacebookLogin = () => {
    InititateFacebookAuth()
  }

  return (
    <main className="w-full h-screen bg-white">
      {loading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 backdrop-blur-sm">
          <Loader />
        </div>
      )}

      <section
        className="relative w-full h-full bg-center bg-cover"
      >
        <img
          className="absolute top-0 left-0 object-cover w-full h-full"
          alt="login background"
          src={LoginBg}
        />

        <article className="flex flex-col w-[496px] items-center justify-center gap-5 p-6 absolute top-[210px] left-1/2 transform -translate-x-1/2 rounded-3xl overflow-hidden border border-solid border-[#d7d7d7] shadow-[0px_14px_24px_#00000014] bg-gradient-to-b from-white via-white to-[#e9e7ff]">
          <header className="flex flex-col items-center justify-center w-full gap-5">
            <div className="inline-flex items-center justify-center px-5 py-0 bg-white rounded-[7px] border border-solid border-[#ded8e1] shadow-[0px_1.75px_1.75px_#00000014]">
              <h1 className="text-[#6d65ed] text-2xl font-bold tracking-[-0.72px] leading-[33.6px] font-plus-jakarta">
                soma
              </h1>
            </div>

            <div className="flex flex-col items-center justify-center w-full gap-2">
              <h2 className="text-black text-2xl text-center font-semibold tracking-[-0.72px] font-sf-pro-display">
                Let&apos;s Get You Logged In
              </h2>
              <p className="text-[#6b7280] text-base text-center tracking-[-0.48px] font-sf-pro-text">
                Run bulk ads on Meta in a few clicks
              </p>
            </div>
          </header>

          <form onSubmit={handleSubmit} className="flex flex-col items-start w-full gap-4 font-sf-pro-text">
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Username"
              size="small"
              value={username}
              onChange={(e) => {
                setUsername(e.target.value)
                if (errors.username) {
                  setErrors({ ...errors, username: "" })
                }
              }}
              error={Boolean(errors.username)}
              helperText={errors.username}
              InputProps={{
                style: {
                  backgroundColor: "#fff",
                  borderRadius: "0.5rem",
                  fontFamily: '"Source Sans Pro", -apple-system, BlinkMacSystemFont, sans-serif',
                },
                classes: {
                  input: "font-sf-pro-text",
                },
              }}
              InputLabelProps={{
                style: {
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                },
              }}
              FormHelperTextProps={{
                style: {
                  fontFamily: '"Source Sans Pro", -apple-system, BlinkMacSystemFont, sans-serif',
                },
              }}
            />
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Password"
              size="small"
              type="password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value)
                if (errors.password) {
                  setErrors({ ...errors, password: "" })
                }
              }}
              error={Boolean(errors.password)}
              helperText={errors.password}
              InputProps={{
                style: {
                  backgroundColor: "#fff",
                  borderRadius: "0.5rem",
                  fontFamily: '"Source Sans Pro", -apple-system, BlinkMacSystemFont, sans-serif',
                },
                classes: {
                  input: "font-sf-pro-text",
                },
              }}
              InputLabelProps={{
                style: {
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                },
              }}
              FormHelperTextProps={{
                style: {
                  fontFamily: '"Source Sans Pro", -apple-system, BlinkMacSystemFont, sans-serif',
                },
              }}
            />
            <button
              type="button"
              className="w-full text-center text-[#6b7280] text-base tracking-[-0.48px] cursor-pointer hover:text-[#6d65ed] bg-transparent border-0 font-sf-pro-text"
              onClick={loginWithOtp}
            >
              Log in via OTP instead
            </button>
          </form>

          <div className="flex flex-col items-start w-full gap-3">
            <button
              type="submit"
              className="w-full py-3 bg-[#6d65ed] text-white text-base font-medium text-center rounded-lg border border-solid border-[#00000026] tracking-[-0.48px] leading-[22.4px] disabled:opacity-70 font-sf-pro-text"
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? "Logging in..." : "Log In via Password"}
            </button>

            <button
              type="button"
              className="w-full flex items-center justify-center gap-2.5 py-3 bg-white text-[#1a77f2] text-base font-medium text-center rounded-lg border border-solid border-[#1a77f2] tracking-[-0.48px] leading-[22.4px] disabled:opacity-70 font-sf-pro-text"
              onClick={handleFacebookLogin}
              disabled={loading}
            >
              <img className="w-[20.07px] h-5" alt="Facebook" src={facebook} />
              Log In Using Facebook
            </button>
          </div>
        </article>
      </section>
    </main>
  )
}

export default LoginPage
